import React, { useState } from 'react';
import { useMutation } from 'react-apollo';

import type { FooterSliceV2Props } from '@ww-digital/web-palette-react/dist/components/Slice/FooterSliceV2/FooterSliceV2';

import { FooterSliceV2 } from '@ww-digital/web-palette-react/dist/components/Slice/FooterSliceV2/FooterSliceV2';

import FooterSliceV2SubscribeMutation from './graphql/FooterSliceV2SubscribeMutation.graphql';

interface FooterSliceV2ContainerProps {
  slice: FooterSliceV2Props;
}

interface SubscribeToNewsletterResult {
  subscribeToNewsletter: {
    success: boolean;
    alreadySubscribed: boolean;
    invalidEmail: boolean;
  };
}

export const FooterSliceV2Container = ({
  slice,
}: FooterSliceV2ContainerProps): JSX.Element => {
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fallbackErrorMessage =
    slice.subscribeEmailErrorMessage ||
    'Something went wrong. Please try again.';

  const [subscribeToNewsletter, { loading }] =
    useMutation<SubscribeToNewsletterResult>(FooterSliceV2SubscribeMutation, {
      errorPolicy: 'all',
    });

  const handleNewsletterSubmit = async (email: string) => {
    if (!email) return;

    setSuccess(false);
    setErrorMessage('');

    try {
      const result = await subscribeToNewsletter({
        variables: {
          email,
          campaign: slice.newsletterCampaign,
          firstName: slice.newsletterFirstName,
          lastName: slice.newsletterLastName,
        },
      });

      const data = result?.data?.subscribeToNewsletter;
      const hasError = result?.errors && result.errors.length > 0;

      if (hasError) {
        console.error(
          '[FooterSliceV2] subscribeToNewsletter GQL errors:',
          result.errors,
        );
        setErrorMessage(fallbackErrorMessage);
        return;
      }

      if (!data) {
        console.error(
          '[FooterSliceV2] subscribeToNewsletter: no data returned',
        );
        setErrorMessage(fallbackErrorMessage);
        return;
      }

      if (data.invalidEmail) {
        console.warn('[FooterSliceV2] subscribeToNewsletter: invalid email');
        setErrorMessage(slice.regexErrorMessage || fallbackErrorMessage);
        return;
      }

      if (!data.success) {
        console.warn(
          '[FooterSliceV2] subscribeToNewsletter: success=false, alreadySubscribed=',
          data.alreadySubscribed,
        );
        setErrorMessage(fallbackErrorMessage);
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error('[FooterSliceV2] subscribeToNewsletter exception:', err);
      setErrorMessage(fallbackErrorMessage);
    }
  };

  return (
    <FooterSliceV2
      {...slice}
      onNewsletterSubmit={handleNewsletterSubmit}
      newsletterLoading={loading}
      newsletterSuccess={success}
      newsletterErrorMessage={errorMessage}
    />
  );
};
